import type { MRT_TableInstance, MRT_TableState } from '@talentmesh/table';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { IdentifiedProfileResponse } from '../../../api/DTO/IdentifiedProfileResponse';
import useAISearchProgress from '../../../api/useAISearchProgress';
import { useIdentifiedListContext } from './IdentifiedListContext';
import useColumns from './useColumns/useColumns';
import useManualSorting from './useManualSorting';
import { useShadcnTable } from '@/Hooks';

type IdentifiedTableState = Partial<MRT_TableState<IdentifiedProfileResponse>>;
type LocalStorageTableState = { tableVersion?: number } & IdentifiedTableState;
type MRTStateMap = Map<string, LocalStorageTableState>;
type IdentifiedTableReturnContext = MRT_TableInstance<IdentifiedProfileResponse>;

// increment when table state is changed, e.g. a new column was added
const currentTableVersion = 3;
const mrtStateKey = 'aiSearchIdentifiedMrtState';

export const IdentifiedTableContext = createContext<IdentifiedTableReturnContext | undefined>(undefined);

export const useIdentifiedTableContextProvider = (): IdentifiedTableReturnContext => {
    const { data: aiProgress, isValidating: isAiProgressValidating } = useAISearchProgress();
    const { data, isLoading: isListLoading, isValidating } = useIdentifiedListContext();
    const isLoading =
        isListLoading ||
        // check for ai progress's progress because the list rely on it to fetch the data
        isAiProgressValidating ||
        aiProgress?.isInProgress !== false;
    const { assessmentId } = useParams();
    const columns = useColumns();
    const [sorting, setSorting] = useManualSorting();

    const initialState = useMemo<LocalStorageTableState | undefined>(() => {
        const mrtStateStr = localStorage.getItem(mrtStateKey);

        if (mrtStateStr) {
            const mrtStateMap: MRTStateMap = new Map(JSON.parse(mrtStateStr));
            const tableState = mrtStateMap.get(assessmentId!);

            if (tableState?.tableVersion === currentTableVersion) {
                return mrtStateMap.get(assessmentId!) || undefined;
            } else {
                localStorage.removeItem(mrtStateKey);
            }
        }

        return undefined;
    }, []);

    const table = useShadcnTable({
        columns,
        data: data?.data,
        state: { showSkeletons: isLoading, isLoading: false, sorting },
        isValidating,
        initialState,
        onSortingChange: setSorting,
    });

    useEffect(() => {
        return () => {
            let mrtStateMap: MRTStateMap = new Map();
            const mrtStateStr = localStorage.getItem(mrtStateKey);

            if (mrtStateStr) {
                mrtStateMap = new Map(JSON.parse(mrtStateStr));
            }

            const tableState: LocalStorageTableState = {
                ...table.getState(),
                rowSelection: undefined,
                tableVersion: currentTableVersion,
            };
            mrtStateMap.set(assessmentId!, tableState);

            localStorage.setItem(mrtStateKey, JSON.stringify(Array.from(mrtStateMap.entries())));
        };
    }, []);

    return table;
};

export const useIdentifiedTableContext = () => {
    const context = useContext(IdentifiedTableContext);
    if (!context) {
        throw new Error('useIdentifiedTableContext must be used within its provider');
    }
    return context;
};
